import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import IconButton from "../Common/Button/IconButton";
import FormFieldRow from "./FormFieldRow";
import { getAssetMetadata, getAssetErrorMessage } from "../../action/asset";
import {
  mapMetadataEntriesToAssetTags,
  normalizeAssetCustomProperties,
  parseAssetMetadataResponse,
  validateAssetCustomPropertyValue,
} from "../../utils/inAppNotificationUtils";
import {
  EMPTY_ASSET_CUSTOM_PROPERTY,
  IN_APP_NOTIFICATION_VALIDATIONS,
  PAGE_CONFIG_ACTION_BUTTON_WIDTH,
  compactInputSx,
  pageAssetIdControlRowSx,
  pageConfigActionButtonSx,
  pageConfigActionRowSx,
  pageConfigDeleteSlotSx,
  pageFormInlineActionSx,
  pageFormLabelSx,
  pageFormShortInputSx,
  pageSubsectionInputRowSx,
  pageSubsectionLabelSx,
} from "../../constants/inAppNotificationConstant";

const getCustomPropertyPairError = (pair = {}) => {
  const key = String(pair.key ?? "").trim();
  const value = String(pair.value ?? "").trim();

  if (key && !value) return "Value is required";
  if (value && !key) return "Key is required";

  const valueError = validateAssetCustomPropertyValue(value);
  if (valueError) return valueError;

  return "";
};

const AssetFields = ({
  assets,
  onChange,
  errors = {},
  required = false,
  idPrefix = "asset",
  feedbackHandler,
  compact = true,
  page = false,
  paired = false,
  readOnly = false,
  alignActionRows = false,
}) => {
  const [lookupId, setLookupId] = useState(assets.asset ?? "");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [lastFetchedAssetId, setLastFetchedAssetId] = useState("");
  const fetchInFlightRef = useRef(null);
  const assetsRef = useRef(assets);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  useEffect(() => {
    setLookupId(assets.asset ?? "");
  }, [assets.asset]);

  const fieldCompact = page ? false : compact;
  const inputSx = page ? pageFormShortInputSx : compactInputSx;
  const customProperties = normalizeAssetCustomProperties(assets?.customProperties);
  const customPropertiesError = errors.assetCustomProperties || "";
  const maxCustomProperties = IN_APP_NOTIFICATION_VALIDATIONS.maxAssetCustomProperties;
  const canAddCustomProperty = !readOnly && customProperties.length < maxCustomProperties;
  const canRemoveCustomProperty = !readOnly && customProperties.length > 0;
  const useAlignedActionLayout = page && (alignActionRows || customProperties.length > 0);
  const customPropertyInputSx = page
    ? { width: "100%", minWidth: 0, maxWidth: "100%" }
    : { ...compactInputSx, maxWidth: "100%" };

  const updateAssets = useCallback(
    (patch) => {
      const current = assetsRef.current || {};
      const nextAssets = {
        asset: current.asset ?? "",
        assetTag: current.assetTag ?? "",
        resolutions: Array.isArray(current.resolutions) ? current.resolutions : [],
        tags: Array.isArray(current.tags) ? current.tags : [],
        customProperties: normalizeAssetCustomProperties(current.customProperties),
        ...patch,
      };
      assetsRef.current = nextAssets;
      onChange(nextAssets);
    },
    [onChange],
  );

  const applySelectedTag = useCallback(
    (tagEntries, preferredTag = assets?.assetTag) => {
      const tags = mapMetadataEntriesToAssetTags(tagEntries);

      if (!tags.length) {
        updateAssets({
          tags: [],
          assetTag: "",
          resolutions: [],
        });
        return;
      }

      const selected =
        tags.find((entry) => entry.assetTag === preferredTag) || tags[0];

      updateAssets({
        tags,
        assetTag: selected.assetTag,
        resolutions: selected.resolutions || [],
      });
    },
    [assets?.assetTag, updateAssets],
  );

  const fetchMetadata = useCallback(
    async (assetId, { silent = false } = {}) => {
      const trimmedId = assetId?.trim();
      if (!trimmedId) {
        if (!silent) {
          feedbackHandler?.({ open: true, type: "error", msg: "Asset ID is required" });
        }
        return;
      }

      setFetchLoading(true);
      setLastFetchedAssetId(trimmedId);
      fetchInFlightRef.current = trimmedId;

      try {
        const response = await getAssetMetadata(trimmedId);
        const entries = parseAssetMetadataResponse(response?.data);

        if (!entries.length) {
          updateAssets({
            asset: "",
            tags: [],
            assetTag: "",
            resolutions: [],
          });
          if (!silent) {
            feedbackHandler?.({
              open: true,
              type: "error",
              msg: "Asset not found. Please verify the Asset ID.",
            });
          }
          return;
        }

        const tags = mapMetadataEntriesToAssetTags(entries);
        const preferredTag = assetsRef.current?.assetTag;
        const selected =
          tags.find((entry) => entry.assetTag === preferredTag) || tags[0] || {
            assetTag: "",
            resolutions: [],
          };

        updateAssets({
          asset: trimmedId,
          tags,
          assetTag: selected.assetTag || "",
          resolutions: selected.resolutions || [],
        });

        if (!silent) {
          feedbackHandler?.({
            open: true,
            type: "success",
            msg: "Asset metadata fetched successfully",
          });
        }
      } catch (error) {
        updateAssets({
          asset: "",
          tags: [],
          assetTag: "",
          resolutions: [],
        });
        if (!silent) {
          feedbackHandler?.({
            open: true,
            type: "error",
            msg: getAssetErrorMessage(
              error,
              "Failed to fetch asset metadata",
              "Asset not found. Please verify the Asset ID.",
            ),
          });
        }
      } finally {
        if (fetchInFlightRef.current === trimmedId) {
          fetchInFlightRef.current = null;
        }
        setFetchLoading(false);
      }
    },
    [feedbackHandler, updateAssets],
  );

  useEffect(() => {
    if (
      fetchLoading ||
      !assets?.asset?.trim() ||
      lastFetchedAssetId === assets.asset ||
      fetchInFlightRef.current
    ) {
      return;
    }
    fetchMetadata(assets.asset, { silent: true });
  }, [assets?.asset, fetchLoading, fetchMetadata, lastFetchedAssetId]);

  const handleFetchClick = () => {
    fetchMetadata(lookupId);
  };

  const preventBlurBeforeClick = (event) => {
    event.preventDefault();
  };

  const handleLookupIdChange = (value) => {
    setLookupId(value);
    updateAssets({
      asset: "",
      tags: [],
      assetTag: "",
      resolutions: [],
    });
    setLastFetchedAssetId("");
  };

  const handleCustomPropertyChange = (index, field, value) => {
    const nextValue =
      field === "value" ? String(value ?? "").replace(/,/g, "") : value;

    const nextProperties = normalizeAssetCustomProperties(customProperties).map(
      (pair, pairIndex) =>
        pairIndex === index
          ? {
              ...pair,
              [field]: nextValue,
            }
          : pair,
    );
    updateAssets({ customProperties: nextProperties });
  };

  const addCustomProperty = () => {
    if (customProperties.length >= maxCustomProperties) return;
    updateAssets({
      customProperties: [
        ...normalizeAssetCustomProperties(customProperties),
        { ...EMPTY_ASSET_CUSTOM_PROPERTY },
      ],
    });
  };

  const removeCustomProperty = (index) => {
    updateAssets({
      customProperties: normalizeAssetCustomProperties(customProperties).filter(
        (_, pairIndex) => pairIndex !== index,
      ),
    });
  };

  const displayTags = assets.tags?.length
    ? assets.tags
    : assets.assetTag
      ? [{ assetTag: assets.assetTag, resolutions: assets.resolutions || [] }]
      : [];

  const assetIdInput = (
    <FormInput
      id={`${idPrefix}-asset-id${readOnly ? "-readonly" : ""}`}
      size="small"
      fullWidth
      value={readOnly ? (assets.asset ?? "") : lookupId}
      onChange={readOnly ? undefined : (e) => handleLookupIdChange(e.target.value)}
      onBlur={
        readOnly
          ? undefined
          : () => {
              const trimmedId = lookupId.trim();
              if (trimmedId && trimmedId !== lastFetchedAssetId) {
                fetchMetadata(trimmedId, { silent: true });
              }
            }
      }
      placeholder="Enter asset ID"
      error={!!errors.asset}
      disabled={readOnly}
      sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}
      slotProps={{
        htmlInput: {
          maxLength: IN_APP_NOTIFICATION_VALIDATIONS.assetId.maxLength,
        },
      }}
    />
  );

  const assetIdControl = readOnly ? (
    <Box sx={{ width: "100%", ...inputSx }}>{assetIdInput}</Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "wrap",
        gap: 0.75,
        alignItems: { xs: "stretch", sm: "flex-start" },
        width: "100%",
        minWidth: 0,
        maxWidth: page ? pageAssetIdControlRowSx.maxWidth : "100%",
      }}
    >
      <FormInput
        id={`${idPrefix}-asset-id`}
        size="small"
        fullWidth
        value={lookupId}
        onChange={(e) => handleLookupIdChange(e.target.value)}
        onBlur={() => {
          const trimmedId = lookupId.trim();
          if (trimmedId && trimmedId !== lastFetchedAssetId) {
            fetchMetadata(trimmedId, { silent: true });
          }
        }}
        placeholder="Enter asset ID"
        error={!!errors.asset}
        sx={{ flex: "1 1 160px", minWidth: 0, maxWidth: "100%" }}
        slotProps={{
          htmlInput: {
            maxLength: IN_APP_NOTIFICATION_VALIDATIONS.assetId.maxLength,
          },
        }}
      />
      <AppButton
        variant="primary"
        size="small"
        loading={fetchLoading}
        onMouseDown={preventBlurBeforeClick}
        onClick={handleFetchClick}
        sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "flex-start" }, whiteSpace: "nowrap" }}
      >
        Fetch Asset Data
      </AppButton>
    </Box>
  );

  const metadataBlock =
    displayTags.length > 0 ? (
      <Box sx={{ minWidth: 0, mt: page ? 0.75 : 0 }}>
        {displayTags.length > 1 && !readOnly && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Select a tag for this notification. Payload uses one assetTag and its resolutions per screen.
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {displayTags.map((entry, index) => {
            const isSelected = assets.assetTag === entry.assetTag;
            const isSelectable = displayTags.length > 1 && !readOnly;

            return (
              <Box
                key={`${entry.assetTag || "entry"}-${index}`}
                onClick={() => {
                  if (isSelectable && entry.assetTag) {
                    updateAssets({
                      assetTag: entry.assetTag,
                      resolutions: entry.resolutions || [],
                    });
                  }
                }}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  bgcolor: isSelected ? "action.hover" : "transparent",
                  cursor: isSelectable && entry.assetTag ? "pointer" : "default",
                }}
              >
                <Typography variant="body2" sx={{ mb: entry.resolutions?.length ? 0.5 : 0, fontSize: "0.8125rem" }}>
                  <Typography component="span" fontWeight={600}>
                    Tag:
                  </Typography>{" "}
                  {entry.assetTag || "—"}
                </Typography>
                {entry.resolutions?.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {entry.resolutions.map((resolution) => (
                      <Chip key={resolution} label={resolution} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    ) : null;

  const addCustomPropertyButton = canAddCustomProperty ? (
    <AppButton
      variant="primary"
      size="small"
      startIcon={<AddIcon />}
      onClick={addCustomProperty}
      sx={
        page && customProperties.length > 0
          ? pageConfigActionButtonSx
          : page
            ? pageFormInlineActionSx
            : { flexShrink: 0, whiteSpace: "nowrap" }
      }
    >
      Add Property
    </AppButton>
  ) : null;

  const renderCustomPropertyInputs = (pair, index, showPairError) => (
    <>
      <FormInput
        id={`${idPrefix}-custom-key-${index}`}
        size="small"
        fullWidth
        placeholder="eg: Enter assetType"
        value={pair.key}
        onChange={(e) => handleCustomPropertyChange(index, "key", e.target.value)}
        disabled={readOnly}
        error={showPairError && !pair.key.trim()}
        sx={customPropertyInputSx}
        slotProps={{
          htmlInput: {
            maxLength: IN_APP_NOTIFICATION_VALIDATIONS.assetCustomPropertyKey.maxLength,
          },
        }}
      />
      <FormInput
        id={`${idPrefix}-custom-value-${index}`}
        size="small"
        fullWidth
        placeholder="eg: image/animation"
        value={pair.value}
        onChange={(e) => handleCustomPropertyChange(index, "value", e.target.value)}
        disabled={readOnly}
        error={
          showPairError &&
          (!pair.value.trim() || !!validateAssetCustomPropertyValue(pair.value))
        }
        sx={customPropertyInputSx}
        slotProps={{
          htmlInput: {
            maxLength: IN_APP_NOTIFICATION_VALIDATIONS.assetCustomPropertyValue.maxLength,
          },
        }}
      />
    </>
  );

  const renderCustomPropertyDelete = (index) =>
    canRemoveCustomProperty ? (
      <IconButton
        variant="danger"
        size="small"
        tooltip="Remove property"
        icon={<DeleteIcon fontSize="small" />}
        onClick={() => removeCustomProperty(index)}
        sx={{ flexShrink: 0, width: 40, height: 40, justifySelf: "center" }}
        aria-label="Remove property"
      />
    ) : (
      !readOnly && (page ? <Box sx={pageConfigDeleteSlotSx} aria-hidden /> : <Box />)
    );

  const additionalPropertiesFields = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, width: "100%", minWidth: 0 }}>
      {customProperties.length === 0 && readOnly && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          —
        </Typography>
      )}

      {customProperties.length === 0 && !readOnly && (
        <Box sx={page ? { ...pageSubsectionInputRowSx, width: "100%" } : undefined}>
          {addCustomPropertyButton}
        </Box>
      )}

      {customProperties.map((pair, index) => {
        const pairError = getCustomPropertyPairError(pair);
        const showPairError = Boolean(customPropertiesError && pairError);
        const showAddBesideFirst = !readOnly && canAddCustomProperty && index === 0;

        if (page) {
          return (
            <Box key={`${idPrefix}-custom-property-${index}`} sx={pageConfigActionRowSx}>
              {renderCustomPropertyInputs(pair, index, showPairError)}
              {renderCustomPropertyDelete(index)}
              {showAddBesideFirst ? (
                addCustomPropertyButton
              ) : (
                !readOnly && (
                  <Box
                    sx={{
                      width: { sm: PAGE_CONFIG_ACTION_BUTTON_WIDTH },
                      height: 40,
                      display: { xs: "none", sm: "block" },
                    }}
                    aria-hidden
                  />
                )
              )}
            </Box>
          );
        }

        return (
          <Box
            key={`${idPrefix}-custom-property-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              width: "100%",
              minWidth: 0,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: canRemoveCustomProperty
                    ? "minmax(0, 1fr) minmax(0, 1fr) 40px"
                    : "minmax(0, 1fr) minmax(0, 1fr)",
                },
                gap: 1,
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                width: "100%",
              }}
            >
              {renderCustomPropertyInputs(pair, index, showPairError)}
              {renderCustomPropertyDelete(index)}
            </Box>
            {showAddBesideFirst && addCustomPropertyButton}
          </Box>
        );
      })}
    </Box>
  );

  const additionalPropertiesBlock = (
    <Box sx={{ width: "100%", minWidth: 0, mt: page ? 1 : 0 }}>
      <Typography variant="body2" sx={{ ...pageFormLabelSx, mb: 0.75 }}>
        Additional Properties (Optional) ({customProperties.length}/{maxCustomProperties})
      </Typography>
      {additionalPropertiesFields}
      {customPropertiesError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {customPropertiesError}
        </Typography>
      )}
    </Box>
  );

  if (page && paired) {
    return (
      <>
        <Box sx={{ ...pageSubsectionLabelSx, gridArea: "assetLabel" }}>
          <Typography variant="body2" sx={pageFormLabelSx}>
            Asset ID
            {required && (
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            ...(useAlignedActionLayout
              ? {
                  gridArea: "assetControls",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  minWidth: 0,
                }
              : {
                  ...pageSubsectionInputRowSx,
                  gridArea: "assetControls",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minHeight: 0,
                  ...pageAssetIdControlRowSx,
                }),
          }}
        >
          {useAlignedActionLayout ? (
            <Box sx={pageConfigActionRowSx}>
              <Box sx={{ gridColumn: { xs: "1", sm: "1 / 3" }, minWidth: 0, width: "100%" }}>
                {assetIdInput}
              </Box>
              {!readOnly && <Box sx={pageConfigDeleteSlotSx} aria-hidden />}
              {!readOnly && (
                <AppButton
                  variant="primary"
                  size="small"
                  loading={fetchLoading}
                  onMouseDown={preventBlurBeforeClick}
                  onClick={handleFetchClick}
                  sx={pageConfigActionButtonSx}
                >
                  Fetch Asset Data
                </AppButton>
              )}
            </Box>
          ) : (
            <Box sx={{ ...pageSubsectionInputRowSx, minHeight: 40, width: "100%" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>{assetIdInput}</Box>
              {!readOnly && (
                <AppButton
                  variant="primary"
                  size="small"
                  loading={fetchLoading}
                  onMouseDown={preventBlurBeforeClick}
                  onClick={handleFetchClick}
                  sx={pageFormInlineActionSx}
                >
                  Fetch Asset Data
                </AppButton>
              )}
            </Box>
          )}
          {errors.asset && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {errors.asset}
            </Typography>
          )}
        </Box>
        {metadataBlock && (
          <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, mt: 0.5 }}>{metadataBlock}</Box>
        )}
        <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>{additionalPropertiesBlock}</Box>
      </>
    );
  }

  if (page) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minWidth: 0 }}>
        <Box sx={pageSubsectionLabelSx}>
          <Typography variant="body2" sx={pageFormLabelSx}>
            Asset ID
            {required && (
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            )}
          </Typography>
        </Box>
        {useAlignedActionLayout ? (
          <Box sx={pageConfigActionRowSx}>
            <Box sx={{ gridColumn: { xs: "1", sm: "1 / 3" }, minWidth: 0, width: "100%" }}>
              {assetIdInput}
            </Box>
            {!readOnly && <Box sx={pageConfigDeleteSlotSx} aria-hidden />}
            {!readOnly && (
              <AppButton
                variant="primary"
                size="small"
                loading={fetchLoading}
                onMouseDown={preventBlurBeforeClick}
                onClick={handleFetchClick}
                sx={pageConfigActionButtonSx}
              >
                Fetch Asset Data
              </AppButton>
            )}
          </Box>
        ) : (
          <Box sx={{ ...pageSubsectionInputRowSx, ...pageAssetIdControlRowSx }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{assetIdInput}</Box>
            {!readOnly && (
              <AppButton
                variant="primary"
                size="small"
                loading={fetchLoading}
                onMouseDown={preventBlurBeforeClick}
                onClick={handleFetchClick}
                sx={{ flexShrink: 0, whiteSpace: "nowrap", alignSelf: "center", ...pageFormInlineActionSx }}
              >
                Fetch Asset Data
              </AppButton>
            )}
          </Box>
        )}
        {errors.asset && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
            {errors.asset}
          </Typography>
        )}
        {metadataBlock}
        {additionalPropertiesBlock}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 1 : 1.25 }}>
      <FormFieldRow compact={fieldCompact} page={page} label="Asset ID" required={required} error={errors.asset}>
        {assetIdControl}
      </FormFieldRow>
      {metadataBlock && (
        <FormFieldRow compact={fieldCompact} page={page} label="Metadata">
          {metadataBlock}
        </FormFieldRow>
      )}
      <FormFieldRow
        compact={fieldCompact}
        page={page}
        label={`Additional Properties (${customProperties.length}/${maxCustomProperties})`}
        hint="Optional"
        error={customPropertiesError}
        stacked
      >
        {additionalPropertiesFields}
      </FormFieldRow>
    </Box>
  );
};

export default AssetFields;
